/*
THESIS: The current processing state leads; destructive actions follow the task.
OWN-WORLD: intoshort's white, blue and Inter preset; flat outlined surfaces.
STORY: Identify the video and check processing progress and remaining time.
FIRST VIEWPORT: Compact back navigation, title and status, then a filling progress bar
and remaining-time estimate. Cancellation sits below with its deletion warning.
FORM: Progress and remaining time, pinned by the user's choice.
*/
import { useCallback, useEffect, useRef, useState } from "react"
import { Link, createFileRoute } from "@tanstack/react-router"
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Check,
  Mail,
  Download,
  EyeOff,
  ExternalLink,
  Info,
  LockKeyhole,
  MoreHorizontal,
  PencilLine,
  RotateCcw,
  X,
} from "lucide-react"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button, buttonVariants } from "@/components/ui/button"
import { TikTokConnectDialog } from "@/components/app/tiktok-connect-dialog"
import { ExperienceSurvey } from "@/components/app/experience-survey"
import { SavingHoursSection } from "@/components/saving-hours-section"
import { ClipPotentialAssessment } from "@/components/app/clip-potential"
import type { ClipPotential } from "@/components/app/clip-potential"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import { Separator } from "@/components/ui/separator"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Skeleton } from "@/components/ui/skeleton"
import { Spinner } from "@/components/ui/spinner"
import { Progress } from "@/components/ui/progress"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { videoClient } from "@/lib/video-client"
import type { ProjectStorage } from "@/components/app/project-storage-notice"
import {
  abortVideoUpload,
  startVideoUpload,
  useVideoUpload,
} from "@/lib/video-upload"
import type { VideoUpload } from "@/lib/video-upload"

let activePreview: HTMLVideoElement | null = null

export const Route = createFileRoute("/app/videos/$videoId")({
  component: VideoPage,
})

type Clip = {
  id: string
  locked: boolean
  title: string
  startSeconds: number
  endSeconds: number
  durationSeconds: number
  quality: "normal" | "weak"
  reason: string
  potential: ClipPotential
  transcript: string | null
  removed: boolean
  partNumber: number
  seriesId: string | null
  origin: "ai" | "fallback" | null
  subtitlePreset: string
  renderStatus: string
  errorMessage: string | null
  renderRevision: number
  renderedRevision: number
  hasExport: boolean
  framingSummary: {
    stackedSeconds: number
    singleSeconds: number
    preservedSeconds: number
    centeredSeconds: number
    uncertainSeconds: number
  } | null
}

type Video = {
  storage: ProjectStorage
  id: string
  title: string
  sourceUrl: string | null
  status: "uploading" | "queued" | "processing" | "ready" | "failed"
  stage: "ingest" | "transcribe" | "select" | "render" | null
  error: { code: string; message: string | null } | null
  notificationStatus: string | null
  renderJobStatus: "queued" | "running" | "completed" | "failed" | null
  canRetryTranscription: boolean
  transcriptionDegraded: boolean | null
  subtitlesUnavailable: boolean | null
  framingDegraded: boolean | null
  processingProgress?: {
    completedPercent: number | null
    remainingMinutes: number | null
    estimatedPercent?: number | null
    active?: boolean
    elapsedSeconds?: number | null
    stageElapsedSeconds?: number | null
    elapsedBasis?: "processing" | "submission"
    estimationStatus?: "available" | "unavailable" | "overdue"
    secondaryActivity?:
      | "Syncing captions"
      | "Performing quality checks"
      | "Reframing video"
      | "Processing captions"
      | "Finalizing"
      | null
  }
  clips: Clip[]
}

function VideoPage() {
  const { videoId } = Route.useParams()
  const navigate = Route.useNavigate()
  const upload = useVideoUpload(videoId)
  const [cancelling, setCancelling] = useState(false)
  const [deletionRequested, setDeletionRequested] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)
  const cancellationInFlight = useRef(false)
  const [video, setVideo] = useState<Video | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const requestVersion = useRef(0)

  const load = useCallback(async () => {
    if (cancellationInFlight.current) return
    const version = requestVersion.current
    try {
      const result = await videoClient.videos.get({ videoId })
      if (version === requestVersion.current) {
        setVideo(result)
        setError(null)
      }
    } catch (failure) {
      if (version === requestVersion.current) {
        if (
          failure &&
          typeof failure === "object" &&
          "code" in failure &&
          failure.code === "CONFLICT"
        ) {
          setDeletionRequested(true)
          setVideo(null)
          setError(null)
        } else {
          setError(
            "We could not load this video. Refresh the page to try again."
          )
        }
      }
    } finally {
      if (version === requestVersion.current) setLoading(false)
    }
  }, [videoId])

  useEffect(() => {
    requestVersion.current += 1
    setVideo(null)
    setError(null)
    setDeletionRequested(false)
    setCancelError(null)
    setLoading(true)
    void load()
  }, [load])

  useEffect(() => {
    const refreshAccess = () => void load()
    window.addEventListener("focus", refreshAccess)
    window.addEventListener("billing-plan-updated", refreshAccess)
    return () => {
      window.removeEventListener("focus", refreshAccess)
      window.removeEventListener("billing-plan-updated", refreshAccess)
    }
  }, [load])

  useEffect(() => {
    const awaitingNotification =
      video?.status === "ready" &&
      ["queued", "running"].includes(video.notificationStatus ?? "")
    const renderingUpdatedExports = ["queued", "running"].includes(
      video?.renderJobStatus ?? ""
    )
    if (
      cancelling ||
      deletionRequested ||
      !video ||
      (["ready", "failed"].includes(video.status) &&
        !awaitingNotification &&
        !renderingUpdatedExports &&
        !video.storage.temporary)
    )
      return
    const refreshInterval = ["ready", "failed"].includes(video.status) && !awaitingNotification && !renderingUpdatedExports ? 30_000 : 5_000
    const timer = window.setInterval(() => void load(), refreshInterval)
    return () => window.clearInterval(timer)
  }, [load, video, cancelling, deletionRequested])

  useEffect(() => {
    if (upload?.phase === "completed") void load()
  }, [upload?.phase, load])

  async function cancelVideo() {
    if (cancellationInFlight.current) return
    cancellationInFlight.current = true
    requestVersion.current += 1
    setCancelling(true)
    setCancelError(null)
    try {
      abortVideoUpload(videoId)
      if (video?.status === "uploading")
        await videoClient.videos.cancelUpload({ videoId })
      await videoClient.videos.cancelAndDelete({ videoId })
      await navigate({ to: "/app" })
    } catch (failure) {
      const code =
        failure && typeof failure === "object" && "code" in failure
          ? failure.code
          : null
      if (code === "NOT_FOUND") {
        await navigate({ to: "/app" })
        return
      }
      setDeletionRequested(true)
      setVideo(null)
      setError(null)
      setCancelError(
        failure &&
          typeof failure === "object" &&
          "message" in failure &&
          typeof failure.message === "string"
          ? failure.message
          : "Deletion could not be completed. Retry to finish removing this video and its files."
      )
    } finally {
      cancellationInFlight.current = false
      setCancelling(false)
    }
  }

  return (
    <main className="app-main app-video-page">
      <Link
        to="/app"
        className={buttonVariants({
          variant: "ghost",
          size: "sm",
          className: "app-video-back self-start",
        })}
      >
        <ArrowLeft data-icon="inline-start" aria-hidden="true" />
        Back to videos
      </Link>
      {(deletionRequested || (video && !loading)) && (
        <div className="app-video-heading">
          <div className="app-video-heading-copy">
            <h1>Your shorts{video ? ` (${video.clips.length})` : ""}</h1>
            {video && (
              <p className="app-video-source">
                Generated from{" "}
                {video.sourceUrl && /^https?:\/\//i.test(video.sourceUrl) ? (
                  <a
                    href={video.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {video.title} <ExternalLink aria-hidden="true" />
                    <span className="sr-only"> (opens in a new tab)</span>
                  </a>
                ) : (
                  <span>{video.title}</span>
                )}
              </p>
            )}
          </div>
          {video && video.status !== "ready" && (
            <Button
              variant="destructive"
              disabled={cancelling}
              onClick={() => void cancelVideo()}
            >
              {cancelling ? (
                <Spinner data-icon="inline-start" />
              ) : (
                <X data-icon="inline-start" aria-hidden="true" />
              )}
              {cancelling ? "Cancelling…" : "Cancel processing"}
            </Button>
          )}
        </div>
      )}
      {video && !loading && !deletionRequested && !cancelling && <ExperienceSurvey />}
      {error && (
        <Alert variant="destructive" className="app-page-alert">
          <AlertCircle aria-hidden="true" />
          <AlertTitle>Unable to load the video</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {(deletionRequested || cancelling) && (
        <Alert variant="info" role="status">
          <Info aria-hidden="true" />
          <AlertTitle>
            {cancelling
              ? "Cancelling and deleting video"
              : "Deletion scheduled"}
          </AlertTitle>
          <AlertDescription>
            {cancelError ||
              "Processing is stopping before the video and its files are permanently removed."}
          </AlertDescription>
        </Alert>
      )}
      {deletionRequested || cancelling ? null : error && !video ? (
        <Button variant="outline" onClick={() => void load()}>
          Try again
        </Button>
      ) : loading || !video ? (
        <VideoSkeleton />
      ) : (
        <VideoContent video={video} onRefresh={load} upload={upload} />
      )}
    </main>
  )
}

function VideoSkeleton() {
  return (
    <div className="app-video-skeleton">
      <Skeleton className="h-12 w-3/4" />
      <Skeleton className="h-28 w-full" />
      <Skeleton className="h-80 w-full" />
    </div>
  )
}

function VideoContent({
  video,
  onRefresh,
  upload,
}: {
  video: Video
  upload: VideoUpload | null
  onRefresh: () => Promise<void>
}) {
  const [retryingTranscription, setRetryingTranscription] = useState(false)
  const [retryTranscriptionError, setRetryTranscriptionError] = useState<
    string | null
  >(null)
  const isReady = video.status === "ready"
  const hasUnavailableShorts = video.clips.some(
    (clip) => clip.renderStatus === "failed"
  )
  const renderJobActive = ["queued", "running"].includes(
    video.renderJobStatus ?? ""
  )
  async function retryTranscription() {
    if (retryingTranscription) return
    setRetryingTranscription(true)
    setRetryTranscriptionError(null)
    try {
      await videoClient.videos.retryTranscription({ videoId: video.id })
      await onRefresh()
    } catch (failure) {
      setRetryTranscriptionError(
        failure &&
          typeof failure === "object" &&
          "message" in failure &&
          typeof failure.message === "string"
          ? failure.message
          : "Transcription could not be retried. Try again."
      )
    } finally {
      setRetryingTranscription(false)
    }
  }
  return (
    <>
      {retryTranscriptionError && (
        <Alert variant="destructive" className="app-page-alert">
          <AlertCircle aria-hidden="true" />
          <AlertTitle>Unable to retry transcription</AlertTitle>
          <AlertDescription>{retryTranscriptionError}</AlertDescription>
        </Alert>
      )}
      {video.canRetryTranscription && (
        <Button
          type="button"
          variant="outline"
          disabled={retryingTranscription}
          onClick={() => void retryTranscription()}
        >
          {retryingTranscription && <Spinner data-icon="inline-start" />}
          {retryingTranscription
            ? "Retrying transcription…"
            : "Retry transcription"}
        </Button>
      )}
      {video.status === "failed" ? (
        <>
          <ProcessingStatus
            status={video.status}
            upload={upload}
            progress={video.processingProgress}
            stage={video.stage}
            notificationStatus={video.notificationStatus}
          />
          <Alert variant="destructive" className="app-page-alert">
            <AlertCircle aria-hidden="true" />
            <AlertTitle>Processing did not finish</AlertTitle>
            <AlertDescription>
              {video.error?.message || "The processing job did not finish."}
            </AlertDescription>
          </Alert>
          {video.clips.length > 0 && (
            <ReviewResults
              clips={video.clips}
              videoId={video.id}
              notificationStatus={video.notificationStatus}
              onRefresh={onRefresh}
              videoReady={false}
              renderJobActive={renderJobActive}
              subtitlesUnavailable={video.subtitlesUnavailable === true}
              hasUnavailableShorts={hasUnavailableShorts}
            />
          )}
        </>
      ) : !isReady ? (
        <>
          <ProcessingStatus
            status={video.status}
            upload={upload}
            progress={video.processingProgress}
            stage={video.stage}
            notificationStatus={video.notificationStatus}
          />
          {video.status === "uploading" && upload?.phase === "error" && (
            <Alert variant="destructive" className="app-page-alert">
              <AlertCircle aria-hidden="true" />
              <AlertTitle>Upload could not finish</AlertTitle>
              <AlertDescription>
                <p>{upload.error}</p>
                <Button
                  variant="outline"
                  onClick={() => void startVideoUpload(video.id)}
                >
                  Try again
                </Button>
              </AlertDescription>
            </Alert>
          )}
          {video.status === "uploading" && !upload && (
            <Alert className="app-page-alert">
              <AlertTitle>Upload incomplete</AlertTitle>
              <AlertDescription>
                The local file is no longer available in this tab. Cancel this
                upload, then import the video again.
              </AlertDescription>
            </Alert>
          )}
          {video.status !== "uploading" && video.clips.length === 0 && (
            <ProcessingNextSteps />
          )}
          {video.clips.length > 0 && (
            <ReviewResults
              clips={video.clips}
              videoId={video.id}
              notificationStatus={video.notificationStatus}
              onRefresh={onRefresh}
              videoReady={false}
              renderJobActive={renderJobActive}
              subtitlesUnavailable={video.subtitlesUnavailable === true}
              hasUnavailableShorts={hasUnavailableShorts}
            />
          )}
        </>
      ) : (
        <>
          <ProcessingStatus
            status={video.status}
            upload={upload}
            progress={video.processingProgress}
            stage={video.stage}
            notificationStatus={video.notificationStatus}
          />
          <ReviewResults
            clips={video.clips}
            videoId={video.id}
            notificationStatus={video.notificationStatus}
            onRefresh={onRefresh}
            videoReady
            renderJobActive={renderJobActive}
            subtitlesUnavailable={video.subtitlesUnavailable === true}
            hasUnavailableShorts={hasUnavailableShorts}
          />
        </>
      )}
    </>
  )
}

function ProcessingStatus({
  status,
  stage,
  progress,
  notificationStatus,
  upload,
}: {
  upload?: VideoUpload | null
  status: Video["status"]
  progress: Video["processingProgress"]
  stage: Video["stage"]
  notificationStatus: string | null
}) {
  if (status === "ready" && notificationStatus === "completed") return null
  const isReady = status === "ready"
  const isFailed = status === "failed"
  const active = status === "processing" && progress?.active === true
  const localUpload = status === "uploading" && Boolean(upload)
  const uploadFloor =
    upload?.phase === "completed" && (stage === "ingest" || status === "queued")
  const backendPercent = isReady
    ? 100
    : active
      ? (progress.estimatedPercent ?? progress.completedPercent ?? null)
      : (progress?.completedPercent ?? null)
  const completedPercent = localUpload
    ? (upload?.percent ?? 0) / 100
    : uploadFloor
      ? Math.max(1, backendPercent ?? 0)
      : backendPercent
  const displayPercent =
    completedPercent === null ? null : Math.round(completedPercent)
  const stageLabels = {
    ingest: "Initializing",
    transcribe: "Transcribing audio",
    select: "Choosing best parts",
    render: "Creating short clips",
  }
  const processingLabel =
    progress?.secondaryActivity ??
    (stage ? stageLabels[stage] : "Waiting to start")
  const statusLabel = isReady
    ? "Ready to review"
    : isFailed
      ? "Processing stopped"
      : status === "uploading"
        ? localUpload
          ? "Initializing"
          : "Upload incomplete"
        : status === "queued"
          ? "Queued"
          : processingLabel
  const showPercent =
    displayPercent !== null &&
    (isReady || status === "processing" || localUpload)
  const label = `${showPercent ? `${displayPercent}% ` : ""}${statusLabel}`
  const remainingMinutes = progress?.remainingMinutes ?? null
  const estimateLabel = isReady
    ? "Your shorts are ready to review."
    : isFailed
      ? "See the error below for details."
      : status === "uploading"
        ? upload?.phase === "uploading"
          ? `Uploading source: ${upload.percent}%.`
          : upload?.phase === "finalizing" || upload?.phase === "completed"
            ? "Confirming your upload…"
            : upload?.phase === "error"
              ? "See the upload error below to try again."
              : localUpload
                ? "Preparing your upload…"
                : "The upload has not been completed."
        : status === "queued"
          ? "Waiting for a worker."
          : progress?.active === false
            ? "Waiting for processing to resume."
            : active && progress.estimationStatus === "overdue"
              ? "Taking longer than expected. Processing continues."
              : remainingMinutes === 0
                ? "Finishing processing…"
                : remainingMinutes === null
                  ? "Estimating time remaining…"
                  : `About ${remainingMinutes} ${remainingMinutes === 1 ? "minute" : "minutes"} remaining.`
  const timeLabel = estimateLabel
  return (
    <Card
      className="app-processing-card overflow-visible shadow-none"
      data-processing-status={status}
    >
      <CardContent className="app-processing-details">
        <Progress
          className="app-generation-progress app-processing-progress"
          value={completedPercent}
          data-active={active && completedPercent === null ? "true" : undefined}
          aria-label="Estimated video processing progress"
          aria-valuetext={`${label}. ${timeLabel}`}
          aria-live="polite"
        >
          <div className="app-processing-progress-content" aria-hidden="true">
            {showPercent && (
              <span className="app-processing-percent">
                {displayPercent}
                <span>%</span>
              </span>
            )}
            <div className="app-processing-progress-copy">
              <span className="app-processing-status">{statusLabel}</span>
              <p className="app-generation-time">{timeLabel}</p>
            </div>
            {isReady && <Check className="app-processing-complete" />}
          </div>
        </Progress>
      </CardContent>
      {!isReady && !isFailed && (
        <CardFooter className="app-processing-footer">
          <Mail aria-hidden="true" />
          <p className="app-processing-note">
            {status === "uploading"
              ? "Keep this tab open until the upload is confirmed. You can visit other pages in the app; refreshing or closing this tab interrupts the upload."
              : notificationStatus === "failed"
                ? "Your results will still be available here if processing succeeds, but the readiness email could not be sent."
                : notificationStatus === "disabled"
                  ? "Email notifications are disabled. Keep your worker running and return here to review the results."
                  : "You can leave this page. We'll email you when your shorts are ready."}
          </p>
        </CardFooter>
      )}
    </Card>
  )
}

function ProcessingNextSteps() {
  return (
    <SavingHoursSection
      className="app-processing-next"
      activateOnHover={false}
      label="While you wait"
      heading="What happens when your shorts are ready"
      description="Review your drafts, make each short yours, then export or post."
    />
  )
}

function ReviewResults({
  clips,
  videoId,
  notificationStatus,
  onRefresh,
  videoReady,
  renderJobActive,
  subtitlesUnavailable,
  hasUnavailableShorts,
}: {
  clips: Clip[]
  videoId: string
  notificationStatus: string | null
  onRefresh: () => Promise<void>
  videoReady: boolean
  renderJobActive: boolean
  subtitlesUnavailable: boolean
  hasUnavailableShorts: boolean
}) {
  const visible = clips.filter((clip) => !clip.removed)
  const removed = clips.filter((clip) => clip.removed)
  return (
    <section className="app-results" aria-label="Proposed shorts">
      {hasUnavailableShorts && (
        <Alert className="app-page-alert">
          <AlertCircle aria-hidden="true" />
          <AlertTitle>Some shorts are unavailable</AlertTitle>
          <AlertDescription>
            Retry the failed render from the affected short.
          </AlertDescription>
        </Alert>
      )}
      {videoReady &&
        (notificationStatus === "queued" ||
          notificationStatus === "running") && (
          <p className="app-processing-note">
            Your readiness email is being sent.
          </p>
        )}
      {visible.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <EyeOff />
            </EmptyMedia>
            <EmptyTitle>
              {clips.length === 0
                ? "No passages were selected"
                : "No active shorts"}
            </EmptyTitle>
            <EmptyDescription>
              {clips.length === 0
                ? "No reviewable passage was generated from this video."
                : "Restore a removed suggestion below if you want to review it again."}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className="app-clip-grid">
          {visible.map((clip) => clip.locked ? (
            <LockedClipCard
              key={clip.id}
              clip={clip}
              videoId={videoId}
            />
          ) : (
            <ClipCard
              key={clip.id}
              clip={clip}
              videoId={videoId}
              onRefresh={onRefresh}
              videoReady={videoReady}
              renderJobActive={renderJobActive}
              subtitlesUnavailable={subtitlesUnavailable}
            />
          ))}
        </div>
      )}
      {removed.length > 0 && (
        <>
          <Separator />
          <div className="app-removed-heading">
            <h2>Removed suggestions</h2>
            <span>Not deleted from storage</span>
          </div>
          <div className="app-clip-grid app-removed-grid">
            {removed.map((clip) => clip.locked ? (
              <LockedClipCard
                key={clip.id}
                clip={clip}
                videoId={videoId}
              />
            ) : (
              <ClipCard
                key={clip.id}
                clip={clip}
                videoId={videoId}
                onRefresh={onRefresh}
                videoReady={videoReady}
                renderJobActive={renderJobActive}
                subtitlesUnavailable={subtitlesUnavailable}
              />
            ))}
          </div>
        </>
      )}
    </section>
  )
}

/*
THESIS: A locked preview matching accessible shorts, with one upgrade action.
OWN-WORLD: Existing neutral shadcn surfaces, Inter and the primary action color.
STORY: Recognize the limit, understand the upgrade, continue to Billing.
FIRST VIEWPORT: The standard vertical preview beside centered copy and a primary CTA.
FORM: Local Operate refinement; preserve all access rules and surrounding cards.
*/
function LockedClipCard({
  clip,
  videoId,
}: {
  clip: Clip
  videoId: string
}) {
  const previewRef = useRef<HTMLDivElement>(null)
  const [inView, setInView] = useState(false)
  const [posterUrl, setPosterUrl] = useState<string | null>(null)
  const [posterError, setPosterError] = useState(false)
  const [posterLoading, setPosterLoading] = useState(false)
  const [posterAttempt, setPosterAttempt] = useState(0)

  useEffect(() => {
    const element = previewRef.current
    if (!element) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true)
          observer.disconnect()
        }
      },
      { rootMargin: "200px" }
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    setPosterUrl(null)
    setPosterError(false)
    setPosterLoading(false)
    if (!inView || clip.renderedRevision < 1) return
    let active = true
    setPosterLoading(true)
    void videoClient.clips.getPosterUrl({ videoId, clipId: clip.id })
      .then(({ url }) => {
        if (active) setPosterUrl(url)
      })
      .catch(() => {
        if (active) setPosterError(true)
      })
      .finally(() => {
        if (active) setPosterLoading(false)
      })
    return () => {
      active = false
    }
  }, [inView, videoId, clip.id, clip.renderedRevision, posterAttempt])

  return (
    <Card className="app-clip-card app-clip-locked">
      <CardContent className="app-clip-preview app-clip-locked-preview rounded-md">
        <div className="app-preview-frame" ref={previewRef}>
          {posterUrl && (
            <img
              src={posterUrl}
              alt=""
              aria-hidden="true"
              className="app-clip-locked-poster"
              referrerPolicy="no-referrer"
              onError={() => {
                setPosterUrl(null)
                setPosterError(true)
              }}
            />
          )}
          <div className="app-clip-lock-label flex flex-col items-center gap-3">
            <Badge variant="secondary">
              <LockKeyhole data-icon="inline-start" aria-hidden="true" />
              Preview locked
            </Badge>
            {posterLoading && <Spinner aria-label="Loading short thumbnail" />}
            {posterError && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPosterAttempt((value) => value + 1)}
              >
                Retry thumbnail
              </Button>
            )}
          </div>
        </div>
      </CardContent>
      <div className="app-clip-details app-clip-unlock-details">
        <CardHeader>
          <CardTitle className="app-clip-unlock-title">
            <h3>Unlock this short</h3>
          </CardTitle>
          <CardDescription>
            Your free trial includes up to 3 shorts. Upgrade to access every
            short from this video.
          </CardDescription>
        </CardHeader>
        <CardFooter>
          <Button render={<Link to="/app/billing" />}>
            Upgrade to unlock
            <ArrowRight data-icon="inline-end" aria-hidden="true" />
          </Button>
        </CardFooter>
      </div>
    </Card>
  )
}

function ClipCard({
  clip,
  videoId,
  onRefresh,
  videoReady,
  renderJobActive,
  subtitlesUnavailable,
}: {
  clip: Clip
  videoId: string
  onRefresh: () => Promise<void>
  videoReady: boolean
  renderJobActive: boolean
  subtitlesUnavailable: boolean
}) {
  const [playback, setPlayback] = useState<{
    kind: "export" | "source"
    url: string
    expiresAt: Date
    startSeconds: number
    endSeconds: number
  } | null>(null)
  const [pending, setPending] = useState<
    "removed" | "download" | "retry" | null
  >(null)
  const [error, setError] = useState<string | null>(null)

  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const previewRequest = useRef(0)
  const previewWasRequested = useRef(false)
  const previousRender = useRef("")
  const previewVideoRef = useRef<HTMLVideoElement>(null)
  useEffect(() => {
    previewRequest.current += 1
    previewWasRequested.current = false
    previousRender.current = ""
    setPlayback(null)
    setPreviewError(null)
    setPreviewLoading(false)
  }, [videoId, clip.id])

  const getPlayback = useCallback(async () => {
    const request = ++previewRequest.current
    setPreviewLoading(true)
    setPreviewError(null)
    try {
      const media = await videoClient.clips.getMediaUrl({
        videoId,
        clipId: clip.id,
        purpose: "playback",
      })
      if (request !== previewRequest.current) return
      setPlayback(media)
    } catch {
      if (request === previewRequest.current)
        setPreviewError("The preview is temporarily unavailable. Try again.")
    } finally {
      if (request === previewRequest.current) setPreviewLoading(false)
    }
  }, [
    videoId,
    clip.id,
    clip.renderStatus,
    clip.renderRevision,
    clip.renderedRevision,
  ])

  useEffect(() => {
    const pausePreview = () => {
      const video = previewVideoRef.current
      video?.pause()
      if (activePreview === video) activePreview = null
    }

    if (clip.removed) pausePreview()

    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") pausePreview()
    }
    document.addEventListener("visibilitychange", onVisibilityChange)
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange)
      pausePreview()
    }
  }, [clip.removed, playback])

  useEffect(() => {
    const render = `${clip.renderStatus}:${clip.renderRevision}:${clip.renderedRevision}`
    if (
      (!previousRender.current || previousRender.current !== render) &&
      (previewWasRequested.current || !previousRender.current)
    ) {
      previewWasRequested.current = true
      void getPlayback()
    }
    previousRender.current = render
    return () => {
      previewRequest.current++
    }
  }, [
    clip.renderStatus,
    clip.renderRevision,
    clip.renderedRevision,
    getPlayback,
  ])

  function requestPreview() {
    previewWasRequested.current = true
    void getPlayback()
  }

  function keepSourceInBounds(element: HTMLVideoElement, restartAtEnd = false) {
    if (playback?.kind !== "source") return
    if (element.currentTime < playback.startSeconds) {
      element.currentTime = playback.startSeconds
      return
    }
    if (element.currentTime >= playback.endSeconds) {
      if (restartAtEnd) {
        element.currentTime = playback.startSeconds
        return
      }
      if (element.currentTime > playback.endSeconds) {
        element.currentTime = playback.endSeconds
      }
      element.pause()
    }
  }

  async function updateRemoved() {
    setPending("removed")
    try {
      await videoClient.clips.setRemoved({
        videoId,
        clipId: clip.id,
        removed: !clip.removed,
      })
      await onRefresh()
    } catch {
      setError("We could not update this suggestion. Try again.")
    } finally {
      setPending(null)
    }
  }

  async function download() {
    setPending("download")
    try {
      const media = await videoClient.clips.getMediaUrl({
        videoId,
        clipId: clip.id,
        purpose: "download",
      })
      window.location.assign(media.url)
    } catch {
      setError("The download link is temporarily unavailable. Try again.")
    } finally {
      setPending(null)
    }
  }

  async function retryRender() {
    setPending("retry")
    setError(null)
    try {
      await videoClient.clips.retryRenders({ videoId, clipId: clip.id })
      await onRefresh()
    } catch {
      setError("We could not queue this render retry. Try again.")
    } finally {
      setPending(null)
    }
  }

  const durationSeconds = Math.max(0, Math.floor(clip.durationSeconds))
  const durationLabel = `${Math.floor(durationSeconds / 60)}:${String(durationSeconds % 60).padStart(2, "0")}`
  const exportReady =
    clip.renderStatus === "ready" &&
    clip.renderedRevision === clip.renderRevision &&
    clip.hasExport
  const hasUpdatedPreview =
    clip.renderStatus !== "ready" ||
    clip.renderedRevision !== clip.renderRevision
  const renderActive =
    clip.renderStatus === "pending" || clip.renderStatus === "rendering"

  return (
    <Card
      className={
        clip.removed ? "app-clip-card app-clip-removed" : "app-clip-card"
      }
    >
      <CardContent className="app-clip-preview rounded-md">
        <div className="app-preview-frame">
          {playback ? (
            <video
              ref={previewVideoRef}
              controls
              muted
              playsInline
              preload="metadata"
              aria-label={`${playback.kind === "source" ? "Source" : "Export"} preview of ${clip.title}`}
              src={playback.url}
              onLoadedMetadata={(event) => {
                event.currentTarget.currentTime =
                  playback.kind === "source" ? playback.startSeconds : 0.001
              }}
              onPlay={(event) => {
                const video = event.currentTarget
                if (document.visibilityState !== "visible" || clip.removed) {
                  video.pause()
                  return
                }
                if (activePreview && activePreview !== video)
                  activePreview.pause()
                activePreview = video
                keepSourceInBounds(video, true)
              }}
              onPause={(event) => {
                if (activePreview === event.currentTarget) activePreview = null
              }}
              onSeeking={(event) => keepSourceInBounds(event.currentTarget)}
              onTimeUpdate={(event) => keepSourceInBounds(event.currentTarget)}
              onEnded={(event) => keepSourceInBounds(event.currentTarget)}
              onError={() => {
                if (activePreview === previewVideoRef.current)
                  activePreview = null
                setPlayback(null)
                setPreviewError("The preview could not be played. Try again.")
              }}
            >
              <track kind="captions" />
            </video>
          ) : previewLoading ? (
            <div role="status" aria-label="Loading preview">
              <Skeleton className="aspect-[9/16] w-full" />
              <span className="sr-only">Loading preview…</span>
            </div>
          ) : (
            <Button variant="secondary" onClick={requestPreview}>
              Load preview
            </Button>
          )}
        </div>
      </CardContent>
      <div className="app-clip-details">
        <CardHeader>
          <div>
            <CardTitle className="text-xl font-semibold">
              {clip.title} ({durationLabel})
            </CardTitle>
          </div>
          <div className="flex flex-wrap gap-2">
            {clip.origin === "fallback" && (
              <Badge variant="outline">Fallback passage</Badge>
            )}
            {clip.quality === "weak" && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger
                    aria-label="What does Weak match mean?"
                    render={
                      <Badge
                        variant="outline"
                        className="min-h-6 cursor-pointer"
                        render={<button type="button" />}
                      />
                    }
                  >
                    Weak match
                    <Info data-icon="inline-end" aria-hidden="true" />
                  </TooltipTrigger>
                  <TooltipContent align="start">
                    This passage was kept as a suggestion, but its editorial
                    relevance is uncertain. Review it before exporting.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </CardHeader>
        <CardContent className="app-clip-copy">
          {playback?.kind === "source" && (
            <p className="app-processing-note" role="status">
              Playing the original source for this passage. It has no subtitles
              or vertical framing.
            </p>
          )}
          {subtitlesUnavailable && (
            <p className="app-processing-note" role="status">
              Subtitles are unavailable for this short because no transcript
              could be created.
            </p>
          )}
          {playback?.kind === "export" && hasUpdatedPreview && (
            <p className="app-processing-note" role="status">
              {clip.renderStatus === "failed"
                ? "Showing the previous export. The updated render failed."
                : "Showing the previous export while the updated preview renders."}
            </p>
          )}
          {previewError && !playback && (
            <Alert>
              <AlertTitle>Preview unavailable</AlertTitle>
              <AlertDescription>
                {previewError}
                <Button
                  variant="secondary"
                  disabled={previewLoading}
                  onClick={requestPreview}
                >
                  Retry preview
                </Button>
              </AlertDescription>
            </Alert>
          )}
          <ClipPotentialAssessment
            potential={clip.potential}
            videoId={videoId}
            clipId={clip.id}
            disabled={!videoReady || renderJobActive || clip.removed}
            onRefresh={onRefresh}
          />
          <section
            className="app-clip-transcript"
            aria-labelledby={`transcript-${clip.id}`}
          >
            <h3 id={`transcript-${clip.id}`}>Transcript</h3>
            <p>
              {clip.transcript === null
                ? "The transcript is unavailable for this short."
                : clip.transcript ||
                  "No speech was transcribed for this short."}
            </p>
          </section>
          {error && (
            <p className="app-data-error" role="status">
              {error}
            </p>
          )}
        </CardContent>
        {renderActive ? (
          <CardFooter className="app-clip-render-status flex-col items-stretch gap-2">
            <Progress
              className="app-generation-progress app-clip-render-progress"
              value={null}
              data-active="true"
              aria-label={`Rendering ${clip.title}`}
              aria-valuetext={
                clip.renderStatus === "pending"
                  ? "This short is queued for rendering."
                  : "Rendering the updated short."
              }
              aria-live="polite"
            >
              <div className="app-generation-progress-label" aria-hidden="true">
                <span className="tracking-tight">
                  {clip.renderStatus === "pending"
                    ? "Render queued…"
                    : clip.hasExport
                      ? "Rendering your changes…"
                      : "Rendering short…"}
                </span>
                <p className="app-generation-time">
                  You can edit another short while this export renders.
                </p>
              </div>
            </Progress>
            {videoReady && (
              <Link
                to="/app/videos/$videoId/clips/$clipId/edit"
                params={{ videoId, clipId: clip.id }}
                className={buttonVariants({ variant: "outline" })}
              >
                <PencilLine data-icon="inline-start" aria-hidden="true" />
                Edit
              </Link>
            )}
          </CardFooter>
        ) : (
          <CardFooter className="app-clip-actions">
            {videoReady ? (
              <Link
                to="/app/videos/$videoId/clips/$clipId/edit"
                params={{ videoId, clipId: clip.id }}
                className={buttonVariants({
                  className: "app-clip-action-edit flex-1",
                })}
              >
                <PencilLine data-icon="inline-start" aria-hidden="true" />
                Edit
              </Link>
            ) : (
              <Button className="app-clip-action-edit flex-1" disabled>
                <PencilLine data-icon="inline-start" aria-hidden="true" />
                Edit
              </Button>
            )}
            {!clip.removed && (
              <TikTokConnectDialog
                videoId={videoId}
                clipId={clip.id}
                clipTitle={clip.title}
                disabled={pending !== null || !exportReady}
                className="flex-1"
              />
            )}
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    variant="outline"
                    size="icon"
                    className="app-clip-action-menu"
                  />
                }
                aria-label={`More actions for ${clip.title}`}
                disabled={pending !== null}
              >
                <MoreHorizontal aria-hidden="true" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuGroup>
                  {!clip.removed && (
                    <DropdownMenuItem
                      disabled={!exportReady}
                      onClick={() => void download()}
                    >
                      <Download aria-hidden="true" />
                      {exportReady
                        ? "Download"
                        : clip.renderStatus === "failed"
                          ? "Unavailable"
                          : "Rendering"}
                    </DropdownMenuItem>
                  )}
                  {!clip.removed &&
                    clip.renderStatus === "failed" &&
                    !renderJobActive && (
                      <DropdownMenuItem onClick={() => void retryRender()}>
                        <RotateCcw aria-hidden="true" />
                        Retry render
                      </DropdownMenuItem>
                    )}
                  <DropdownMenuItem
                    variant={clip.removed ? "default" : "destructive"}
                    onClick={() => void updateRemoved()}
                  >
                    {clip.removed ? (
                      <RotateCcw aria-hidden="true" />
                    ) : (
                      <EyeOff aria-hidden="true" />
                    )}
                    {clip.removed ? "Restore" : "Remove"}
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </CardFooter>
        )}
      </div>
    </Card>
  )
}
